import React, { memo, useEffect, useMemo, useRef, useState } from 'react';

/** Fixed 200×120 score wheel – 50% circle (true semicircle), arc and marker fit inside (no clipping). */

export type TQScoreWheelSmallLevel = {
  min: number;
  max: number;
  color: string;
  fillColor?: string;
};

const WIDTH = 220;
const HEIGHT = 140;
const TOP_PADDING = 6;
const CONTENT_RESERVE = 52;
const ARC_THICKNESS = 6;
const H_PADDING = 10;
/** 50% circle: 180° gap → semicircle arc (left–top–right); vertical span = r only, so fits easily */
const GAP_ANGLE_DEG = 180;
const SEGMENT_GAP_DEG = 8;

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

function drawArc(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  start: number,
  end: number,
  color: string,
  width: number,
) {
  if (end <= start) return;
  ctx.beginPath();
  ctx.lineCap = 'round';
  ctx.lineWidth = width;
  ctx.strokeStyle = color;
  ctx.arc(cx, cy, r, start, end, false);
  ctx.stroke();
}

function polar(cx: number, cy: number, r: number, ang: number) {
  return { x: cx + r * Math.cos(ang), y: cy + r * Math.sin(ang) };
}

function defaultFill(color: string) {
  const m = color.replace('#', '');
  const r = parseInt(m.substring(0, 2), 16);
  const g = parseInt(m.substring(2, 4), 16);
  const b = parseInt(m.substring(4, 6), 16);
  return `rgba(${Number.isNaN(r) ? 170 : r}, ${Number.isNaN(g) ? 170 : g}, ${Number.isNaN(b) ? 170 : b}, 0.25)`;
}

export type TQScoreWheelSmallProps = {
  value: number;
  levels: TQScoreWheelSmallLevel[];
  maxScore: number;
  trackColor?: string;
  animationMs?: number;
};

const TQScoreWheelSmall: React.FC<TQScoreWheelSmallProps> = ({
  value,
  levels,
  maxScore,
  trackColor = 'transparent',
  animationMs = 600,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [animValue, setAnimValue] = useState(value);
  const deviceRatio =
    typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

  const { minAll } = useMemo(() => {
    const mins = levels.map((l) => l.min);
    return { minAll: Math.min(0, ...mins) };
  }, [levels]);

  useEffect(() => {
    const start = animValue;
    const end = clamp(value, minAll, maxScore);
    if (animationMs <= 0 || Math.abs(end - start) < 0.5) {
      setAnimValue(end);
      return;
    }
    let raf = 0;
    const t0 = performance.now();
    const ease = (t: number) => 1 - (1 - t) ** 3;
    const tick = (now: number) => {
      const p = clamp((now - t0) / animationMs, 0, 1);
      setAnimValue(start + (end - start) * ease(p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, minAll, maxScore, animationMs, animValue]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const ratio = deviceRatio;
    const px = WIDTH * ratio;
    const py = HEIGHT * ratio;
    canvas.width = px;
    canvas.height = py;
    canvas.style.width = `${WIDTH}px`;
    canvas.style.height = `${HEIGHT}px`;

    // 50% circle: arc is top semicircle, vertical span = r (not 1.5r)
    const arcRegionHeight = HEIGHT - TOP_PADDING - CONTENT_RESERVE;
    const rCss = arcRegionHeight - ARC_THICKNESS / 2;
    const rCssClamped = Math.min(rCss, WIDTH / 2 - H_PADDING);
    const r = rCssClamped * ratio;
    const cx = (WIDTH / 2) * ratio;
    const cy = (TOP_PADDING + rCssClamped) * ratio;
    const thicknessPx = ARC_THICKNESS * ratio;

    const gap = (GAP_ANGLE_DEG * Math.PI) / 180;
    const segmentGap = (SEGMENT_GAP_DEG * Math.PI) / 180;
    const baseStart = Math.PI / 2 + gap / 2;
    const baseSweep = Math.PI * 2 - gap;

    const total = maxScore - minAll;
    const L = levels.length;
    const totalInterGaps = Math.max(0, L - 1) * segmentGap;
    const drawable = Math.max(0, baseSweep - totalInterGaps);

    const portion = (lo: number, hi: number) =>
      clamp(hi, minAll, maxScore) - clamp(lo, minAll, maxScore);

    function segmentAngleSpan(lo: number, hi: number) {
      return (portion(lo, hi) / total) * drawable;
    }

    const segments = levels.map((lv, idx) => {
      const sweepBefore = levels
        .slice(0, idx)
        .reduce(
          (acc, p, i) =>
            acc + segmentAngleSpan(p.min, p.max) + (i >= 0 ? segmentGap : 0),
          0,
        );
      const start = baseStart + sweepBefore;
      const span = segmentAngleSpan(lv.min, lv.max);
      return { idx, ...lv, start, end: start + span };
    });

    ctx.clearRect(0, 0, px, py);
    ctx.save();

    drawArc(
      ctx,
      cx,
      cy,
      r,
      baseStart,
      baseStart + baseSweep,
      trackColor,
      thicknessPx,
    );

    const currentVal = clamp(animValue, minAll, maxScore);

    segments.forEach((seg) => {
      const fill = seg.fillColor || defaultFill(seg.color);
      drawArc(ctx, cx, cy, r, seg.start, seg.end, fill, thicknessPx);

      if (currentVal <= seg.min) return;
      if (currentVal >= seg.max) {
        drawArc(ctx, cx, cy, r, seg.start, seg.end, seg.color, thicknessPx);
      } else {
        const p = (currentVal - seg.min) / (seg.max - seg.min);
        const mid = seg.start + (seg.end - seg.start) * p;
        drawArc(ctx, cx, cy, r, seg.start, mid, seg.color, thicknessPx);
      }
    });

    const activeSeg =
      segments.find((s) => currentVal >= s.min && currentVal <= s.max) ||
      segments[segments.length - 1];
    let aVal = baseStart;
    if (activeSeg) {
      const frac = clamp(
        (currentVal - activeSeg.min) / (activeSeg.max - activeSeg.min),
        0,
        1,
      );
      aVal = activeSeg.start + (activeSeg.end - activeSeg.start) * frac;
    }

    const m = polar(cx, cy, r, aVal);
    const outer = thicknessPx * 0.52;
    const inner = thicknessPx * 0.28;

    ctx.beginPath();
    ctx.fillStyle = '#FFFFFF';
    ctx.strokeStyle = activeSeg ? activeSeg.color : '#AAAAAA';
    ctx.lineWidth = Math.max(1, 2 * ratio);
    ctx.arc(m.x, m.y, outer, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.fillStyle = activeSeg ? activeSeg.color : '#AAAAAA';
    ctx.arc(m.x, m.y, inner, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }, [animValue, levels, maxScore, trackColor, deviceRatio, minAll]);

  return (
    <canvas
      ref={canvasRef}
      className="tq-score-wheel-small tq-score-wheel-small--canvas"
      style={{ display: 'block', width: WIDTH, height: HEIGHT }}
    />
  );
};

export default memo(TQScoreWheelSmall);
